import type { TourId } from '@sorrem/shared';
import { TOURS } from '@sorrem-client/ui/features/GuidedTour';
import { useIntroCommission, useOfferCutscene, useTourProgress } from '@sorrem-client/ui/hooks';
import { CHARACTER_HOME_TABS, type CharacterHomeTab } from '../tabs/character-home-tabs';

export interface CharacterHomeToursState {
    /** Whether Character Home's page tour may run now. */
    isPageTourEnabled: boolean;
    /** The open tab's tour, once the page tour is done; null while it may not run. */
    tabTourId: TourId | null;
}

/**
 * Which of Character Home's guided tours (#234) may run: the page tour once
 * the home is shown (not character creation) and the intro commission's
 * offer cutscene, if it is still to come, has played; then, once the page
 * tour is done, the open tab's own tour. Each shows once (see `useTour`).
 */
export function useCharacterHomeTours(activeTab: CharacterHomeTab, isHomeShown: boolean): CharacterHomeToursState {
    const { commission: introCommission, isLoading: isIntroLoading } = useIntroCommission();
    const offerCutscene = useOfferCutscene();
    const pageTour = useTourProgress('character-home', TOURS['character-home'].version);

    // The clerk's offer plays on the Overview tab before anything else (see CommissionOfferProvider).
    const isOfferPending = introCommission?.status === 'offered' && offerCutscene.cutsceneId !== null;
    const isPageReady = isHomeShown && !isIntroLoading && !offerCutscene.isLoading && !isOfferPending;

    return {
        isPageTourEnabled: isPageReady,
        tabTourId: isPageReady && pageTour.isDone ? CHARACTER_HOME_TABS[activeTab].tourId : null,
    };
}
